import {
  buildBreakLineBoundingTriangle,
  makeStairPlanOutlines,
} from "building-generation/elements/stairSystem/stairSystems";
import { solidWallWithOpenings } from "building-generation/elements/walls/solidWall";
import { solidWallCrossSection } from "building-generation/elements/walls/solidWallCrossSection";
import { getWallPath } from "building-generation/elements/walls/wall";
import { makeStairWellWallMassSystem } from "building-generation/utils/floorplan";
import type { StairSystemHandle } from "building-types/handles/docEntityHandleTypes";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { SheetGeometryCategory } from "building-types/views/sheetGeometryCategory";
import type { SheetGeometry } from "building-types/views/sheetGeometryTypes";
import { sheetPolylineGeometryNew } from "building-types/views/sheetGeometryTypes";
import { WallType } from "building-types/walls/wallEnums";
import { WallLayerType } from "building-types/walls/wallEnums";
import { levelInchesNew, levelInchesToWorld, percentNew, sheetPointsNew } from "common/utils/nominalNumbers";
import { STAIR_CUT_HEIGHT } from "doc/plans/floorplanConstants";
import {
  PlanGeometryBuilder,
  type PlanGenerationContext,
  type PlanGenerationContextBase,
} from "doc/plans/planGeometrySource";
import type { PlanGeometryGeneratorResult } from "doc/plans/planGeometrySourceTypes";
import { roomsOnLevel } from "doc/rooms/roomsOnLevel";
import { aabb2Overlaps } from "geom/aabb2";
import { ZERO_EPSILON } from "geom/core";
import { joinPolyLine2Loops } from "geom/join";
import { offsetPolyLine2 } from "geom/offset";
import { polygonIntersection, polygonSubtract, polygonUnion } from "geom/polygon";
import {
  polyLine2AABB2,
  polyLine2Area,
  polyLine2Copy,
  polyLine2IsClosed,
  polyLine2Reverse,
  polyLine2SetCCW,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { trimRailWithClosedPolyLine } from "geom/trim";
import { docEntityHandleInReportingUnit, reportingUnitRoomRegionBBs } from "lib/house/plans/reportingUnitRooms";

export function makeSheetGeometryFromStairs({
  building,
  levelId,
  stairCutPlaneHeight = levelInchesNew(STAIR_CUT_HEIGHT),
  viewTemplateId,
  selectedReportingUnitLabelId,
}: PlanGenerationContext): PlanGeometryGeneratorResult {
  const builder = new PlanGeometryBuilder();
  const viewTemplate = viewTemplateId ? building.doc.viewTemplates[viewTemplateId] : null;
  const hasStairBreakLine = !!viewTemplate?.categories.find((cat) => cat === SheetGeometryCategory.STAIR_BREAK_LINE);

  const { rooms, levels, stairSystems: stairs, settings } = building;

  const { wall: wallConfig } = settings;

  const stairInReportingUnit = (stairSystem: { id: string }) =>
    docEntityHandleInReportingUnit(building, selectedReportingUnitLabelId, {
      type: "STAIR_SYSTEM",
      id: stairSystem.id,
    });

  const handle = levels[levelId].handle;

  const level = levels[levelId];
  const levelRooms = roomsOnLevel(levelId, rooms, building.roomsByLevel);

  const levelBelowIndex = level.levelBelow ?? "";
  const levelBelow = levels[levelBelowIndex];

  const { floorElevation } = level;
  const floorStairCutHeight = levelInchesToWorld(stairCutPlaneHeight, floorElevation);

  // visible stair linework on level below
  if (levelBelow) {
    const openToBelowOutlines: PolyLine2[] = [];

    for (const room of levelRooms) {
      if (room.type === RoomType.OPEN_BELOW || room.type === RoomType.STAIRS || room.type === RoomType.STAIRS_EDITOR) {
        openToBelowOutlines.push(polyLine2Copy(room.outline));
      }
    }

    if (openToBelowOutlines.length > 0) {
      const openBelowOutlinesJoined: PolyLine2[] = polygonUnion(openToBelowOutlines.map((x) => [x])).map((x) => x[0]);

      for (const openBelowOutline of openBelowOutlinesJoined) {
        const openBelowOutlineExpanded = offsetPolyLine2(
          polyLine2SetCCW(openBelowOutline),
          -wallConfig.interiorWallOffset,
        );
        const openBelowOutlineBB = polyLine2AABB2(openBelowOutlineExpanded);

        for (const stairId in stairs) {
          const stairSystem = stairs[stairId];
          if (stairSystem.levelId !== levelBelowIndex) {
            continue;
          }
          if (!stairInReportingUnit(stairSystem)) {
            continue;
          }

          const { solidPolys } = makeStairPlanOutlines(stairSystem, floorStairCutHeight);

          for (const solidPoly of solidPolys) {
            const solidPolyBB = polyLine2AABB2(solidPoly);

            if (!aabb2Overlaps(openBelowOutlineBB, solidPolyBB)) {
              continue;
            }

            const newOutline = polygonIntersection([[solidPoly], [openBelowOutlineExpanded]]).map((x) => x[0]);

            builder.addGeometry(
              sheetPolylineGeometryNew({
                handle,
                paths: newOutline,
                fill: false,
                fillOpacity: percentNew(1),
                strokeOpacity: percentNew(1),
              }),
              SheetGeometryCategory.STAIR_PROJECTED,
            );
          }
        }
      }
    }
  }

  for (const stairId in stairs) {
    const stairSystem = stairs[stairId];
    if (stairSystem.levelId !== levelId) {
      continue;
    }
    if (!stairInReportingUnit(stairSystem)) {
      continue;
    }

    const stairPlanOutlines = makeStairPlanOutlines(stairSystem, floorStairCutHeight, true);
    const { cutLinePolys } = stairPlanOutlines;
    let {
      dashedPolys,
      solidPolys,
      fillPolys,
      dashedPolysBelowCutPlane,
      dashedPolysAboveCutPlane,
      solidPolysAboveCutPlane,
    } = stairPlanOutlines;
    const stairSystemHandle: StairSystemHandle = { type: "STAIR_SYSTEM", id: stairSystem.id };

    let trimmedCutLinePolys: PolyLine2[] | null = null;

    if (hasStairBreakLine && cutLinePolys.length > 0) {
      const stairOutlines = Object.values(stairSystem.blocks).map((block) => block.outline);
      trimmedCutLinePolys = trimBreakLineAgainstStairOutlines(cutLinePolys, stairOutlines);

      builder.addGeometry(
        sheetPolylineGeometryNew({
          handle: stairSystemHandle,
          paths: trimmedCutLinePolys,
          fill: false,
          fillOpacity: percentNew(0),
          strokeOpacity: percentNew(1),
        }),
        SheetGeometryCategory.STAIR_BREAK_LINE,
      );

      const hasStairNosing = dashedPolysBelowCutPlane.length > 0;

      for (const breakLine of cutLinePolys) {
        const clipRegion = buildBreakLineBoundingTriangle(breakLine);
        const reverseBreakLine = polyLine2Reverse(polyLine2Copy(breakLine));
        const reverseClipRegion = buildBreakLineBoundingTriangle(reverseBreakLine);

        const solidSplit = clipPolysAgainstBreaklineRegion(solidPolys, clipRegion);
        const fillSplit = clipPolysAgainstBreaklineRegion(fillPolys, clipRegion);
        const dashedSplit = clipPolysAgainstBreaklineRegion(dashedPolys, reverseClipRegion);

        fillPolys = [...fillSplit.outside, ...dashedSplit.inside];
        // Handles stairs with nosing, e.g. ASSEMBLED type stairs.
        if (hasStairNosing) {
          const dashedPolysBelowSplit = clipPolysAgainstBreaklineRegion(dashedPolysBelowCutPlane, clipRegion);
          const solidPolysAboveSplit = clipPolysAgainstBreaklineRegion(solidPolysAboveCutPlane, reverseClipRegion);
          const dashedPolysAboveSplit = clipPolysAgainstBreaklineRegion(dashedPolysAboveCutPlane, reverseClipRegion);

          // For stairs with nosing, the dashed lines below the cut plane line up with the dashed lines
          // above the cut plane. The solid polys above the cut plane are added to the breakline region.
          dashedPolysBelowCutPlane = [...dashedPolysBelowSplit.outside, ...dashedPolysAboveSplit.inside];
          dashedPolysAboveCutPlane = [...dashedPolysBelowSplit.inside, ...dashedPolysAboveSplit.outside];
          // FillSplit contains the full stair outlines, while dashedPolysBelowCutPlane contain only treads.
          dashedPolys = [...fillSplit.inside, ...dashedSplit.outside];

          solidPolys = [...solidSplit.outside, ...solidPolysAboveSplit.inside];
          solidPolysAboveCutPlane = [...solidSplit.inside, ...solidPolysAboveSplit.outside];
        } else {
          solidPolys = [...solidSplit.outside, ...dashedSplit.inside];
          dashedPolys = [...solidSplit.inside, ...dashedSplit.outside];
        }
      }
    }

    builder.addGeometry(
      sheetPolylineGeometryNew({
        handle: stairSystemHandle,
        paths: fillPolys,
        fill: true,
        fillOpacity: percentNew(1),
        strokeOpacity: percentNew(0),
        fillColor: "white",
      }),
      SheetGeometryCategory.STAIR_PROJECTED,
    );

    builder.addGeometry(
      sheetPolylineGeometryNew({
        handle: stairSystemHandle,
        paths: solidPolys,
        fill: false,
        fillOpacity: percentNew(0),
        strokeOpacity: percentNew(1),
      }),
      SheetGeometryCategory.STAIR_PROJECTED,
    );

    for (const poly of dashedPolysBelowCutPlane) {
      builder.addGeometry(
        sheetPolylineGeometryNew({
          handle: stairSystemHandle,
          paths: [poly],
          fill: false,
          fillOpacity: percentNew(1),
          strokeOpacity: percentNew(1),
          strokeColor: "white",
        }),
        SheetGeometryCategory.STAIR_PROJECTED,
      );
      builder.addGeometry(
        sheetPolylineGeometryNew({
          handle,
          paths: [poly],
          fill: false,
          fillOpacity: percentNew(1),
          strokeOpacity: percentNew(1),
          dash: true,
          dashMask: true,
          dashLength: [sheetPointsNew(3), sheetPointsNew(1)],
        }),
        SheetGeometryCategory.STAIR_PROJECTED,
      );
    }

    for (const poly of dashedPolys) {
      builder.addGeometry(
        sheetPolylineGeometryNew({
          handle,
          paths: [poly],
          fill: false,
          fillOpacity: percentNew(1),
          strokeOpacity: percentNew(1),
          dashMask: true,
        }),
        SheetGeometryCategory.STAIR_ABOVE,
      );
    }
    if (trimmedCutLinePolys) {
      for (const poly of trimmedCutLinePolys) {
        const maskBreakLineGeom = sheetPolylineGeometryNew({
          handle,
          paths: [poly],
          fill: false,
          strokeColor: "white",
          fillOpacity: percentNew(1),
          strokeOpacity: percentNew(1),
          dash: false,
        });
        builder.addGeometry(maskBreakLineGeom, SheetGeometryCategory.STAIR_ABOVE);
        builder.addGeometry({ ...maskBreakLineGeom }, SheetGeometryCategory.STAIR_PROJECTED);
      }
    }
  }
  return builder;
}

export function makeSheetGeometryFromStairCrossSections({
  building,
  levelId,
  cutPlaneHeight,
  selectedReportingUnitLabelId,
}: PlanGenerationContextBase & Pick<PlanGenerationContext, "selectedReportingUnitLabelId">): SheetGeometry[] {
  const result: SheetGeometry[] = [];
  const { levels, walls, mirror } = building;

  const wallSystem = walls[levelId];
  const wallTable = wallSystem.walls;

  // new wall mass system with only stair walls
  const wallMassSystemStairOnly = makeStairWellWallMassSystem(building, levelId);

  const wallsMassesForStairs = wallMassSystemStairOnly.walls;
  const wallSegments: PolyLine2[] = [];

  const issues: BuildingGenerationIssue[] = [];

  for (const wallId in wallsMassesForStairs) {
    const mass = wallsMassesForStairs[wallId];
    const wall = wallTable[wallId];

    if (mass.wallType !== WallType.SOLID_MASS || wall.wallType !== WallType.SOLID) {
      continue;
    }

    const { doorPlacements: doors, windowPlacements: windows, leftSide, rightSide } = wall;
    const path = getWallPath(wall);

    const solidWall = {
      ...solidWallWithOpenings({
        id: mass.id,
        levelId,
        path,
        mass,
        leftSide,
        rightSide,
        wallMassSystem: wallMassSystemStairOnly,
        mirror,
        issues,
      }),
      // we need to force these - solidWallWithOpenings will discard them
      doors,
      windows,
    };

    wallSegments.push(
      ...solidWallCrossSection({
        wall: solidWall,
        wallLayer: WallLayerType.MASS,
        roomFloorAssemblies: building.roomFloorAssemblies,
        cutPlane: cutPlaneHeight,
      }),
    );
  }

  if (!(issues.length === 0)) {
    console.assert(false, issues);
  }

  const regionBBs = reportingUnitRoomRegionBBs(building, selectedReportingUnitLabelId);
  const stairWellCrossSections = joinPolyLine2Loops(wallSegments).filter((x) => {
    if (polyLine2Area(x) <= ZERO_EPSILON) {
      return false;
    }
    return !regionBBs || regionBBs.some((region) => aabb2Overlaps(polyLine2AABB2(x), region));
  });

  result.push(
    sheetPolylineGeometryNew({
      handle: levels[levelId].handle,
      paths: stairWellCrossSections,
    }),
  );
  return result;
}

/**
 * With the breakline enabled, the stair above/projected geometry outlines
 * must change, since the breakline is rotated 30 degrees relative to the stair
 * split by the cut plane.
 *
 * This function computes the right triangular region above the
 * breakline, and uses it to delete the heterogenously styled projected/above stair geometry
 * within the region. The region is then filled back in with the uniformly
 * styled geometry for the region.
 */
function clipPolysAgainstBreaklineRegion(polys: PolyLine2[], clipRegion: PolyLine2) {
  const inside: PolyLine2[] = [];
  const outside: PolyLine2[] = [];

  for (const poly of polys) {
    if (polyLine2IsClosed(poly)) {
      const insidePieces = polygonIntersection([[poly], [clipRegion]]);
      for (const piece of insidePieces) {
        inside.push(...piece);
      }
      const outsidePieces = polygonSubtract([[poly], [clipRegion]]);
      for (const piece of outsidePieces) {
        outside.push(...piece);
      }
    } else {
      const insideRails: PolyLine2[] = [];
      const outsideRails: PolyLine2[] = [];
      trimRailWithClosedPolyLine(poly, clipRegion, insideRails, outsideRails);
      inside.push(...insideRails);
      outside.push(...outsideRails);
    }
  }

  return { inside, outside };
}

function trimBreakLineAgainstStairOutlines(cutLinePolys: PolyLine2[], allOutlines: PolyLine2[]) {
  const unionedStairOutlines = polygonUnion(allOutlines.map((p) => [p]));
  if (unionedStairOutlines.length <= 0) {
    return cutLinePolys;
  }
  const trimmedCutLinePolys: PolyLine2[] = [];

  for (const breakLine of cutLinePolys) {
    for (const outline of unionedStairOutlines) {
      const insideRails: PolyLine2[] = [];
      trimRailWithClosedPolyLine(breakLine, outline[0], insideRails);
      trimmedCutLinePolys.push(...insideRails);
    }
  }

  return trimmedCutLinePolys;
}
